import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Zap, ArrowRight, ShieldCheck, Cpu, Layers, Activity, Check,
  ChevronDown, Play, Send, Terminal as TerminalIcon, Database, Sparkles,
  Clock, Flame, HelpCircle
} from "lucide-react";
import "./LandingPage.css";

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Pricing Toggle (Monthly vs Yearly)
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // monthly or yearly

  // FAQs Accordion State
  const [activeFaq, setActiveFaq] = useState(null);

  // Live Simulator States
  const [simState, setSimState] = useState("idle"); // idle, tv_signal, fastapi, db_log, mt5_exec, done
  const [simSymbol, setSimSymbol] = useState("XAUUSD");
  const [simAction, setSimAction] = useState("BUY");
  const [simLot, setSimLot] = useState(0.1);
  const [simLogs, setSimLogs] = useState([]);

  // Auto-scroll simulator console
  useEffect(() => {
    const consoleBody = document.getElementById("sim-console-body");
    if (consoleBody) {
      consoleBody.scrollTop = consoleBody.scrollHeight;
    }
  }, [simLogs]);

  // Simulate Webhook Copier Pipeline
  const runSimulation = () => {
    setSimState("tv_signal");
    setSimLogs([]);
    
    // Step 1: Webhook received from TradingView
    const now = () => new Date().toLocaleTimeString();
    
    setSimLogs([
      { time: now(), text: `[TradingView] 🌐 Triggering Alert webhook for ${simSymbol}...`, type: "info" }
    ]);

    setTimeout(() => {
      setSimState("fastapi");
      setSimLogs(prev => [
        ...prev,
        { time: now(), text: `[FastAPI] ⚡ POST /webhook - Payload validation successful.`, type: "success" },
        { time: now(), text: `[FastAPI] 🔍 Resolving active licenses matching "TVMT5-DEMO-LICENSE-KEY"...`, type: "info" }
      ]);
    }, 900);

    setTimeout(() => {
      setSimState("db_log");
      setSimLogs(prev => [
        ...prev,
        { time: now(), text: `[Database] 🔒 Storing alert in 'signals' collection. Status: pending`, type: "db" },
        { time: now(), text: `[Database] 📌 Inserted Document ID: 6f917b2524d34038a79854ef`, type: "db" }
      ]);
    }, 1800);

    setTimeout(() => {
      setSimState("mt5_exec");
      setSimLogs(prev => [
        ...prev,
        { time: now(), text: `[MT5 Bridge] 📡 Polled by Terminal Komal123456 (Login: 2121952).`, type: "info" },
        { time: now(), text: `[MT5 Bridge] ⚡ WebRequest response: 200 OK | Sending signal payload.`, type: "success" },
        { time: now(), text: `[MT5 Terminal] 📈 Expert Advisor executed: ${simAction} ${simLot} lot at market ask.`, type: "mt5" },
        { time: now(), text: `[MT5 Terminal] ✅ Order ticket #7942851 opened successfully! PnL tracked.`, type: "success" }
      ]);
    }, 2800);

    setTimeout(() => {
      setSimState("done");
      setSimLogs(prev => [
        ...prev,
        { time: now(), text: `[Done] 🚀 Bridge copying pipeline completed successfully! (Total latency: 42ms)`, type: "final" }
      ]);
    }, 3800);
  };

  const faqs = [
    {
      q: "MetaTrader 5 / MQL5 mein WebRequest error 5203 kyu aata hai?",
      a: "Error 5203 (ERR_WEBREQUEST_REQUEST_FAILED) tab aata hai jab custom headers mein duplicate/conflicting 'Host' headers pass hote hain ya secure SSL certificates authentic nahi hote. Hamare updated MQL5 EA v6 mein native HTTPS wrappers aur automatic header generation shamil hain jo duplicate headers aur 5203 conflicts ko completely eliminate karte hain."
    },
    {
      q: "Kya is copier EA ko use karne ke liye DLL imports ki jarurat hai?",
      a: "Nahi! Hamara bridge copier 100% native MQL5 WebRequest standards par chalta hai. Kisi bhi custom C++ DLL dependencies ki jarurat nahi hai, jisse aapka terminal 100% secure rehta hai aur safe institutional trading configurations approve hoti hain."
    },
    {
      q: "TradingView alert se MT5 execute hone mein kitna time lagta hai?",
      a: "FastAPI asynchronous thread engine aur local MQL5 polling timers ke high-frequency coordination ki wajah se, master TradingView webhook dispatch hone se client MT5 terminal par order place hone tak ka total average time 50 milliseconds se bhi kam lagta hai."
    },
    {
      q: "Kya main ek license key se multiple MT5 accounts link kar sakta hu?",
      a: "Haan! Professional aur enterprise plans ke andar aap multiple MetaTrader 5 trading accounts (differing logins, brokers or magic numbers) ko ek hi license ID aur dashboard panel ke through manage aur monitor kar sakte hain."
    }
  ];

  const plans = [
    {
      name: "Starter Bridge",
      priceMonthly: 19,
      priceYearly: 15,
      features: [
        "1 Active MT5 Account link",
        "Unlimited signals copied",
        "Sub-50ms native execution",
        "Default Fallback lot controls",
        "Detailed signal execution copies logs",
        "Email support channel"
      ],
      popular: false
    },
    {
      name: "Professional Multiplexer",
      priceMonthly: 39,
      priceYearly: 31,
      features: [
        "Up to 5 MT5 Accounts linked concurrently",
        "Unified Multi-Account multiplexing",
        "Dynamic custom SL & TP controls",
        "Custom Magic numbers tracking",
        "Admin user activity system audits",
        "Premium support + Discord access"
      ],
      popular: true
    },
    {
      name: "Institutional Enterprise",
      priceMonthly: 99,
      priceYearly: 79,
      features: [
        "Unlimited MT5 Account linkages",
        "Dedicated isolated FastAPI backend router",
        "Multi-terminal user-wise filters audit",
        "Custom filling mode configurations",
        "Personalized client webhook simulation",
        "24/7 Phone support + Custom EA setup"
      ],
      popular: false
    }
  ];

  return (
    <div className="landing-container">
      {/* 🔮 Background Canvas Effects */}
      <div className="background-glow" style={{ position: "absolute", top: "10%", left: "20%", width: "400px", height: "400px", background: "rgba(0, 242, 254, 0.05)", borderRadius: "50%", filter: "blur(120px)", pointerEvents: "none", zIndex: 1 }} />
      <div className="background-glow" style={{ position: "absolute", top: "60%", right: "15%", width: "500px", height: "500px", background: "rgba(139, 92, 246, 0.05)", borderRadius: "50%", filter: "blur(150px)", pointerEvents: "none", zIndex: 1 }} />

      {/* 🌐 Interactive Navigation Bar */}
      <header className="landing-nav">
        <Link to="/" className="landing-brand">
          <Zap className="landing-brand-logo" size={22} fill="currentColor" />
          <span>PINE <span className="accent">CONNECTOR</span></span>
        </Link>
        <div className="landing-nav-actions">
          {user ? (
            <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
              Go to Dashboard
              <ArrowRight size={15} />
            </button>
          ) : (
            <>
              <button className="btn btn-ghost" onClick={() => navigate("/login")}>
                Sign In
              </button>
              <button className="btn btn-primary" onClick={() => navigate("/signup")}>
                Get Started
                <ArrowRight size={15} />
              </button>
            </>
          )}
        </div>
      </header>

      {/* 🚀 Hero Banner Section */}
      <section className="landing-hero">
        <div className="landing-badge">
          <Sparkles size={14} fill="currentColor" />
          Native MT5 Bridge EA v6 Live
        </div>
        <h1 className="landing-title">
          Bridge TradingView to <span className="accent">MetaTrader 5</span> Natively
        </h1>
        <p className="landing-hero-desc">
          Automate, scale, and synchronize signals from TradingView to thousands of MT5 client accounts instantly. 
          Zero DLL requirements, sub-50ms execution speed, and comprehensive real-time copying audits.
        </p>
        <div className="landing-hero-ctas">
          {user ? (
            <button className="btn btn-primary" style={{ padding: "14px 28px", fontSize: "15px" }} onClick={() => navigate("/dashboard")}>
              Access Dashboard
              <ArrowRight size={16} />
            </button>
          ) : (
            <>
              <button className="btn btn-primary" style={{ padding: "14px 28px", fontSize: "15px", gap: "10px" }} onClick={() => navigate("/signup")}>
                Get Started Free
                <ArrowRight size={16} />
              </button>
              <button className="btn btn-ghost" style={{ padding: "14px 28px", fontSize: "15px" }} onClick={() => navigate("/login")}>
                Sign In to Panel
              </button>
            </>
          )}
        </div>
      </section>

      {/* 💻 Live Interactive Webhook Copier Simulator Section */}
      <section className="landing-section" style={{ borderTop: "1px solid var(--border)", paddingTop: "100px" }}>
        <div className="section-head">
          <div className="section-title-landing">Interactive Copying Simulator</div>
          <p className="section-desc-landing">
            Check the live animation to see how a signal travels through our hyper-optimized copier architecture to your terminal.
          </p>
        </div>

        <div className="simulator-container">
          {/* Simulator Inputs Controller */}
          <div className="simulator-controller">
            <h3 style={{ fontSize: "18px", fontWeight: "800", color: "var(--accent)" }}>Simulation Console</h3>
            <p style={{ color: "var(--text2)", fontSize: "13.5px", margin: 0 }}>
              Customize your signal inputs and trigger the automated API bridge replication path:
            </p>

            <div className="sim-input-box">
              <div className="form-group" style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Asset Symbol</label>
                <select className="form-input" value={simSymbol} onChange={e => setSimSymbol(e.target.value)} disabled={simState !== "idle" && simState !== "done"}>
                  <option value="XAUUSD">XAUUSD (Gold Spot)</option>
                  <option value="BTCUSD">BTCUSD (Bitcoin)</option>
                  <option value="EURUSD">EURUSD (Euro / US Dollar)</option>
                  <option value="US30">US30 (Dow Jones)</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "16px" }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Action Order Type</label>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    className={`btn ${simAction === "BUY" ? "btn-primary" : "btn-ghost"}`}
                    style={{ flex: 1, padding: "8px" }}
                    onClick={() => setSimAction("BUY")}
                    disabled={simState !== "idle" && simState !== "done"}
                  >
                    BUY / LONG
                  </button>
                  <button
                    className={`btn ${simAction === "SELL" ? "btn-primary" : "btn-ghost"}`}
                    style={{ flex: 1, padding: "8px", background: simAction === "SELL" ? "var(--red)" : "var(--sidebar-user-bg)", border: simAction === "SELL" ? "none" : "1px solid var(--border)", color: simAction === "SELL" ? "#fff" : "var(--text)" }}
                    onClick={() => setSimAction("SELL")}
                    disabled={simState !== "idle" && simState !== "done"}
                  >
                    SELL / SHORT
                  </button>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Execution Volume (Lots)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={simLot}
                  onChange={e => setSimLot(parseFloat(e.target.value) || 0.01)}
                  disabled={simState !== "idle" && simState !== "done"}
                />
              </div>
            </div>

            <button className="sim-btn" onClick={runSimulation} disabled={simState !== "idle" && simState !== "done"}>
              <Play size={16} fill="currentColor" />
              {simState === "idle" || simState === "done" ? "Fire Webhook Alert" : "Processing Signal..."}
            </button>
          </div>

          {/* Simulator Visual Terminal Output */}
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div className="simulator-monitor">
              <div className="monitor-header">
                <div className="monitor-dots">
                  <span className="monitor-dot red" />
                  <span className="monitor-dot yellow" />
                  <span className="monitor-dot green" />
                </div>
                <div className="monitor-title">Live Execution Monitor Console</div>
                <TerminalIcon size={12} style={{ color: "var(--accent)" }} />
              </div>
              <div className="monitor-body" id="sim-console-body">
                {simLogs.length === 0 ? (
                  <div style={{ color: "#4b5563", fontSize: "13px", textAlign: "center", marginTop: "140px", fontStyle: "italic" }}>
                    Console idle. Click "Fire Webhook Alert" to begin...
                  </div>
                ) : (
                  simLogs.map((log, index) => (
                    <div key={index} className="console-line">
                      <span className="console-timestamp">[{log.time}]</span>
                      <span className="console-content" style={{ 
                        color: log.type === "success" 
                          ? "#10b981" 
                          : log.type === "db" 
                            ? "#8b5cf6" 
                            : log.type === "mt5" 
                              ? "#00f2fe" 
                              : log.type === "final"
                                ? "#00f2fe"
                                : "#94a3b8" 
                      }}>
                        {log.text}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Simulated Live Flow Nodes */}
            <div className="sim-flow-container">
              <div className={`flow-node ${simState !== "idle" ? "active" : ""}`}>
                <div className="node-circle">
                  <Send size={15} />
                </div>
                <span>TradingView</span>
              </div>
              <div className={`flow-arrow ${simState === "fastapi" || simState === "db_log" || simState === "mt5_exec" || simState === "done" ? "active" : ""}`} />
              <div className={`flow-node ${simState === "fastapi" || simState === "db_log" || simState === "mt5_exec" || simState === "done" ? "active" : ""}`}>
                <div className="node-circle">
                  <Cpu size={15} />
                </div>
                <span>FastAPI</span>
              </div>
              <div className={`flow-arrow ${simState === "db_log" || simState === "mt5_exec" || simState === "done" ? "active" : ""}`} />
              <div className={`flow-node ${simState === "db_log" || simState === "mt5_exec" || simState === "done" ? "active" : ""}`}>
                <div className="node-circle">
                  <Database size={15} />
                </div>
                <span>MongoDB</span>
              </div>
              <div className={`flow-arrow ${simState === "mt5_exec" || simState === "done" ? "active" : ""}`} />
              <div className={`flow-node ${simState === "mt5_exec" || simState === "done" ? "active" : ""}`}>
                <div className="node-circle">
                  <Activity size={15} />
                </div>
                <span>MT5 EA</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 🛡️ Core Highlights Section */}
      <section className="landing-section" style={{ borderTop: "1px solid var(--border)", paddingTop: "100px" }}>
        <div className="section-head">
          <div className="section-title-landing">Unrivaled High-End Core System</div>
          <p className="section-desc-landing">
            Built using standard, secure, and modern programming protocols to deliver unmatched trading copier stability.
          </p>
        </div>

        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon-wrap">
              <Clock size={22} />
            </div>
            <div className="feature-title">Sub-50ms Total Latency</div>
            <div className="feature-desc">
              FastAPI asynchronous loops and optimized MongoDB indexes execute orders at the speed of light, avoiding slippage.
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrap">
              <ShieldCheck size={22} />
            </div>
            <div className="feature-title">Zero DLL Dependencies</div>
            <div className="feature-desc">
              Runs out of the box using native MQL5 security standard commands. Safe, secure, and approved by all standard brokers.
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrap">
              <Layers size={22} />
            </div>
            <div className="feature-title">Anti-Herd Polling Synchronization</div>
            <div className="feature-desc">
              Synchronized timers with microsecond randomized jitter prevent massive thundering herd spikes, keeping the server balanced.
            </div>
          </div>

          <div className="feature-card">
            <div className="feature-icon-wrap">
              <Flame size={22} />
            </div>
            <div className="feature-title">Automated Closing Actions</div>
            <div className="feature-desc">
              Seamlessly closing buy or sell positions across linked terminals via CLOSE_BUY, CLOSE_SELL, and CLOSE_ALL TradingView commands.
            </div>
          </div>
        </div>
      </section>

      {/* 💸 Pricing and Subscription Plans Section */}
      <section className="landing-section" style={{ borderTop: "1px solid var(--border)", paddingTop: "100px" }}>
        <div className="section-head">
          <div className="section-title-landing">Flexible Flexible Tiers</div>
          <p className="section-desc-landing">
            Select the plan that fits your trading scale. Save 20% by billing annually.
          </p>
        </div>

        {/* Pricing Toggle Billing Period */}
        <div className="pricing-toggle">
          <div className="toggle-pill">
            <div className={`toggle-option ${billingPeriod === "monthly" ? "active" : ""}`} onClick={() => setBillingPeriod("monthly")}>
              Monthly Billing
            </div>
            <div className={`toggle-option ${billingPeriod === "yearly" ? "active" : ""}`} onClick={() => setBillingPeriod("yearly")}>
              Yearly (Save 20%)
            </div>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="pricing-grid">
          {plans.map((plan) => {
            const price = billingPeriod === "yearly" ? plan.priceYearly : plan.priceMonthly;
            return (
              <div key={plan.name} className={`pricing-card ${plan.popular ? "popular" : ""}`}>
                {plan.popular && <div className="popular-badge">Most Popular</div>}
                
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price-row">
                  <span className="plan-currency">$</span>
                  <span className="plan-price">{price}</span>
                  <span className="plan-period">/ month</span>
                </div>

                <ul className="plan-features-list">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="plan-feature-item">
                      <Check className="plan-feature-icon" size={14} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  className={`btn ${plan.popular ? "btn-primary" : "btn-ghost"}`}
                  style={{ marginTop: "auto", width: "100%", padding: "12px", borderRadius: "10px", justifyContent: "center" }}
                  onClick={() => navigate("/signup")}
                >
                  Choose {plan.name.split(" ")[0]}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* 💬 Frequently Asked Questions Accordion Section */}
      <section className="landing-section" style={{ borderTop: "1px solid var(--border)", paddingTop: "100px", paddingBottom: "120px" }}>
        <div className="section-head">
          <div className="section-title-landing" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
            <HelpCircle size={28} className="accent" />
            Frequently Asked Questions
          </div>
          <p className="section-desc-landing">
            Got questions about the TradingView to MT5 bridge system? We have detailed answers.
          </p>
        </div>

        <div className="faqs-container">
          {faqs.map((faq, index) => (
            <div key={index} className={`faq-item ${activeFaq === index ? "active" : ""}`}>
              <div className="faq-head" onClick={() => setActiveFaq(activeFaq === index ? null : index)}>
                <span className="faq-question">{faq.q}</span>
                <ChevronDown className="faq-icon" size={16} />
              </div>
              {activeFaq === index && (
                <div className="faq-body">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 🌐 Futuristic Footer Section */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-column">
            <Link to="/" className="landing-brand" style={{ marginBottom: "12px" }}>
              <Zap className="landing-brand-logo" size={20} fill="currentColor" />
              <span>PINE <span className="accent">CONNECTOR</span></span>
            </Link>
            <p className="footer-desc">
              Sleek, secure, and state-of-the-art trade copying software connecting standard TradingView webhook dispatches to MT5 terminals in milliseconds.
            </p>
          </div>

          <div className="footer-column">
            <div className="footer-title">Platform</div>
            <ul className="footer-links">
              <li><Link to="/signup" className="footer-link">Register Plan</Link></li>
              <li><Link to="/login" className="footer-link">Sign In</Link></li>
              <li><Link to="/downloads" className="footer-link">Download EA</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <div className="footer-title">Resources</div>
            <ul className="footer-links">
              <li><Link to="/docs" className="footer-link">MQL5 Docs</Link></li>
              <li><Link to="/docs" className="footer-link">FastAPI APIs</Link></li>
              <li><Link to="/docs" className="footer-link">Webhook Guide</Link></li>
            </ul>
          </div>

          <div className="footer-column">
            <div className="footer-title">Secure & Online</div>
            <p className="footer-desc" style={{ fontSize: "12px" }}>
              Our server processes signals through isolated Docker sandboxes and strict MongoDB collections, guaranteeing safety and high performance.
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <div>© {new Date().getFullYear()} PINE CONNECTOR Copier. All rights reserved.</div>
          <div>Designed and Optimized with ❤️ by Antigravity AI.</div>
        </div>
      </footer>
    </div>
  );
}
